import React from 'react';
import { occurrenceEvents } from '../../lib/occurrences';

/**
 * The Planner's calendar pieces, shared so every place that shows scheduled time
 * (the Planner, Home's day agenda) looks and behaves the same: event colours,
 * the events built from tasks, Google Calendar, habits and focus blocks, how an event is drawn,
 * and the calendar's styles.
 */

export const getPriorityColors = (isDark) => ({
  p1: { bg: isDark ? '#DC26264D' : '#DC262660', border: '#B91C1C', text: isDark ? '#FFFFFF' : '#000000' },
  p2: { bg: isDark ? '#F59E0B4D' : '#F59E0B60', border: '#D97706', text: isDark ? '#FFFFFF' : '#000000' },
  p3: { bg: isDark ? '#3B82F64D' : '#3B82F660', border: '#2563EB', text: isDark ? '#FFFFFF' : '#000000' },
  p4: { bg: isDark ? '#6B72804D' : '#6B728070', border: '#4B5563', text: isDark ? '#FFFFFF' : '#000000' },
});

// Completed-event palette is theme-aware: the previous hard-coded light-gray
// text was readable on a dark canvas but vanished against a white one when a
// split task had its first session marked done. Dark mode keeps the muted
// gray; light mode swaps to a darker slate so the strikethrough title stays
// legible.
export const getCompletedColors = (isDark) =>
  isDark
    ? { bg: 'rgba(75, 85, 99, 0.3)', border: 'rgba(75, 85, 99, 0.4)', text: 'rgba(156, 163, 175, 0.85)' }
    : { bg: 'rgba(148, 163, 184, 0.25)', border: 'rgba(100, 116, 139, 0.45)', text: 'rgba(51, 65, 85, 0.85)' };

/** The hours the calendar shows: working hours with two hours around them (6 to 22 without settings). */
export const getSlotRange = (workingHours, timezoneOffsetAdjust) => {
  const adj = Math.round(timezoneOffsetAdjust || 0);

  if (!workingHours) {
    const minH = Math.max(0, 6 + adj);
    const maxH = Math.min(24, 22 + adj);
    return {
      slotMinTime: `${String(minH).padStart(2, '0')}:00:00`,
      slotMaxTime: `${String(maxH).padStart(2, '0')}:00:00`,
      minHour: minH, maxHour: maxH
    };
  }

  const { start, end } = workingHours;

  // Parse working hours (format: "HH:MM")
  const [startHour] = start.split(':').map(Number);
  const [endHour] = end.split(':').map(Number);

  // Calculate -2h and +2h with timezone adjustment, clamped to 0-24 range
  const minHour = Math.max(0, startHour - 2 + adj);
  const maxHour = Math.min(24, endHour + 2 + adj);

  return {
    slotMinTime: `${String(minHour).padStart(2, '0')}:00:00`,
    slotMaxTime: `${String(maxHour).padStart(2, '0')}:00:00`,
    minHour,
    maxHour
  };
};

/** FullCalendar events for time-blocked tasks (split sessions too), the coming occurrences of
 * recurring ones (lib/occurrences.js), Google Calendar events, habits and focus blocks. */
export const buildCalendarEvents = ({ visibleTasks, googleEvents, habitEvents, focusEvents = [], priorityColors, completedColors, getProjectName, occurrences = [] }) => {
  const events = [];

  // Include both active and completed tasks (completed shown with faded style)
  visibleTasks
    .filter(task => task.scheduled_start && task.scheduled_end)
    .forEach(task => {
      const isCompleted = task.status === 'completed' || !!task.completed_at;
      // Completed tasks get faded gray colors
      const colors = isCompleted
        ? completedColors
        : (priorityColors[task.priority] || priorityColors.p4);
      const projectName = getProjectName(task.project_id);

      // Check if this is a split task with multiple sessions
      if (task.is_split && task.scheduled_sessions && task.scheduled_sessions.length > 1) {
        // Create an event for each session (skip sessions with missing times)
        task.scheduled_sessions.filter(s => s.start && s.end).forEach((session, index) => {
          // Check if this specific session is completed
          const isSessionCompleted = session.completed || false;
          const sessionColors = (isSessionCompleted || isCompleted)
            ? completedColors
            : (priorityColors[task.priority] || priorityColors.p4);

          events.push({
            id: `${task.id}-session-${index + 1}`,
            title: task.title || '',
            start: session.start,
            end: session.end,
            backgroundColor: sessionColors.bg,
            borderColor: sessionColors.border,
            textColor: sessionColors.text,
            extendedProps: {
              task: task,
              priority: task.priority,
              duration: session.duration,
              projectName: projectName,
              isSplitSession: true,
              sessionNumber: index + 1,
              totalSessions: task.total_sessions,
              isCompleted: isCompleted,
              isSessionCompleted: isSessionCompleted
            }
          });
        });
      } else {
        // Normal single-block task
        events.push({
          id: task.id,
          title: task.title || '',
          start: task.scheduled_start,
          end: task.scheduled_end,
          backgroundColor: colors.bg,
          borderColor: colors.border,
          textColor: colors.text,
          extendedProps: {
            task: task,
            priority: task.priority,
            duration: task.duration,
            projectName: projectName,
            isCompleted: isCompleted
          }
        });
      }
    });

  // Add Google Calendar events
  googleEvents.forEach(event => {
    events.push({
      id: `google-${event.google_event_id}`,
      title: event.title,
      start: event.start,
      end: event.end,
      allDay: event.all_day,
      backgroundColor: '#4285F4',
      borderColor: '#3367D6',
      textColor: '#FFFFFF',
      editable: false,
      extendedProps: {
        isGoogleEvent: true,
        googleEventId: event.google_event_id,
        calendarId: event.calendar_id,
        description: event.description,
        location: event.location
      }
    });
  });

  // Add habit events
  habitEvents.forEach(event => {
    events.push({
      id: `habit-${event.id}`,
      title: event.title,
      start: event.start,
      end: event.end,
      allDay: false,
      backgroundColor: event.color ? `${event.color}33` : '#F59E0B33',
      borderColor: event.color || '#F59E0B',
      textColor: event.color || '#F59E0B',
      editable: false,
      extendedProps: {
        isHabitEvent: true,
        habitId: event.habit_id,
      }
    });
  });

  // Focus blocks (auto-schedule, backend utils/focus_time.py): drawn like habits, in their color
  focusEvents.forEach(event => {
    const color = event.color || '#3B82F6';
    events.push({
      id: `focus-${event.id}`,
      title: event.title,
      start: event.start,
      end: event.end,
      allDay: false,
      backgroundColor: `${color}33`,
      borderColor: color,
      textColor: color,
      editable: false,
      extendedProps: {
        isFocusEvent: true,
      }
    });
  });

  events.push(...occurrenceEvents(occurrences, visibleTasks, {
    colorsFor: (task) => priorityColors[task.priority] || priorityColors.p4,
    projectNameOf: getProjectName,
    timedOnly: true,
  }));

  return events;
};

/** How an event is drawn in the calendar (tasks, split sessions, habits). */
export const createEventRenderer = ({ formatTime, isDark, t }) => (eventInfo) => {

  const task = eventInfo.event.extendedProps.task;
  const projectName = eventInfo.event.extendedProps.projectName;
  const duration = eventInfo.event.extendedProps.duration;
  const start = eventInfo.event.start;
  const end = eventInfo.event.end;
  const isGoogleEvent = eventInfo.event.extendedProps.isGoogleEvent;
  const isHabitEvent = eventInfo.event.extendedProps.isHabitEvent;
  const isFocusEvent = eventInfo.event.extendedProps.isFocusEvent;
  const isCompleted = eventInfo.event.extendedProps.isCompleted;
  const isSessionCompleted = eventInfo.event.extendedProps.isSessionCompleted;
  const durationMs = eventInfo.event.end - eventInfo.event.start;
  const isVeryShort = durationMs <= 30 * 60 * 1000; // 30 min or less
  const isShort = durationMs < 45 * 60 * 1000; // less than 45 min
  const isSplitSession = eventInfo.event.extendedProps.isSplitSession;
  const sessionNumber = eventInfo.event.extendedProps.sessionNumber;
  const totalSessions = eventInfo.event.extendedProps.totalSessions;

  if (isHabitEvent || isFocusEvent) {
    return (
      <div className="px-1 py-0.5 overflow-hidden h-full flex flex-col justify-center">
        <div className="font-medium text-[10px] truncate leading-tight italic">
          {isHabitEvent && '↺ '}{eventInfo.event.title}
        </div>
        {!isVeryShort && (
          <div className="text-[9px] opacity-70 leading-tight">
            {formatTime(start)} - {formatTime(end)}
          </div>
        )}
      </div>
    );
  }

  // Determine if this event should show as completed
  const showAsCompleted = isSplitSession ? isSessionCompleted : isCompleted;

  // Completed events fade the same way in both themes — the previous
  // light-mode-only line-through stayed at 100% opacity, so a completed
  // split session looked just as loud as the upcoming one next to it.
  // Strike-through stays as a secondary cue.
  const completedClass = showAsCompleted ? 'opacity-60 line-through' : '';
  // Part X/Y chip needs theme-aware tinting: bg-accent disappears on
  // a light pink/yellow event surface in light mode.
  const partChipClass = `rounded px-0.5 ${isDark ? 'bg-accent' : 'bg-black/10'}`;
  const textMuted = isDark ? 'opacity-80' : '';
  const textSubtle = isDark ? 'opacity-75' : '';

  if (isVeryShort) {
    return (
      <div className={`px-1 py-0.5 overflow-hidden h-full flex items-center gap-1 ${completedClass}`}>
        <span className="font-medium text-[9px] truncate flex-1">
          {showAsCompleted && <span className="mr-1">✓</span>}
          {task?.title || eventInfo.event.title}
          {isSplitSession && <span className={`ml-1 ${textSubtle}`}>{sessionNumber}/{totalSessions}</span>}
        </span>
        <span className={`text-[8px] flex-shrink-0 ${textMuted}`}>
          {formatTime(start)}
        </span>
      </div>
    );
  }

  return (
    <div className={`px-1 py-0.5 overflow-hidden h-full flex flex-col ${completedClass}`}>
      <div className="font-semibold text-[10px] truncate leading-tight">
        {showAsCompleted && <span className="mr-1">✓</span>}
        {task?.title || eventInfo.event.title}
      </div>
      {!isShort && (
        <>
          <div className={`text-[9px] leading-tight ${textMuted}`}>
            {formatTime(start)} - {formatTime(end)} {duration ? `(${t('ui_common.units.minutes_short', { value: duration })})` : ''}
          </div>
          {isSplitSession && (
            <div className={`text-[9px] leading-tight flex items-center gap-1 ${textMuted}`}>
              <span className={partChipClass}>
                {t('planner.part_label')} {sessionNumber}/{totalSessions}
              </span>
            </div>
          )}
          {projectName && !isGoogleEvent && (
            <div className={`text-[8px] truncate leading-tight ${textSubtle}`}>
              {projectName}
            </div>
          )}
        </>
      )}
      {isShort && !isVeryShort && (
        <div className={`text-[9px] leading-tight flex items-center gap-1 ${textMuted}`}>
          <span>{formatTime(start)} - {formatTime(end)}</span>
          {isSplitSession && (
            <span className={partChipClass}>
              {sessionNumber}/{totalSessions}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

/** The calendar's look in both themes. */
export const PlannerCalendarStyles = ({ isDark, slotHeight }) => (
  <style>{`
              .fc {
                --fc-border-color: ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'};
                --fc-button-bg-color: ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'};
                --fc-button-border-color: ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'};
                --fc-button-hover-bg-color: ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'};
                --fc-button-hover-border-color: ${isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)'};
                --fc-button-active-bg-color: #F59E0B;
                --fc-button-active-border-color: #F59E0B;
                --fc-today-bg-color: rgba(245,158,11,0.1);
                --fc-page-bg-color: transparent;
                --fc-neutral-bg-color: ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'};
                --fc-list-event-hover-bg-color: ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'};
                height: 100%;
              }
              /* One day on screen is all "today": the honey tint only marks today among other days. */
              .fc .fc-timeGridDay-view { --fc-today-bg-color: transparent; }
              .fc .fc-toolbar {
                display: none !important;
              }
              .fc .fc-col-header-cell-cushion,
              .fc .fc-daygrid-day-number,
              .fc .fc-timegrid-slot-label {
                color: ${isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)'};
                font-size: 11px;
              }
              @media (min-width: 640px) {
                .fc .fc-col-header-cell-cushion,
                .fc .fc-daygrid-day-number,
                .fc .fc-timegrid-slot-label {
                  font-size: 12px;
                }
              }
              .fc .fc-daygrid-day.fc-day-today .fc-daygrid-day-number {
                color: #F59E0B;
                font-weight: bold;
              }
              .fc-theme-standard td, .fc-theme-standard th {
                border-color: ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'};
              }
              .fc .fc-timegrid-now-indicator-line {
                border-color: #F59E0B;
              }
              .fc .fc-timegrid-now-indicator-arrow {
                border-color: #F59E0B;
                border-top-color: transparent;
                border-bottom-color: transparent;
              }
              .fc-event {
                cursor: pointer;
                border-radius: 4px;
                border-width: 0 0 0 3px;
                font-size: 10px;
              }
              .fc-event, .fc-event * {
                color: ${isDark ? '#ffffff' : '#000000'} !important;
              }
              @media (min-width: 640px) {
                .fc-event {
                  font-size: 11px;
                }
              }
              .fc-event:hover {
                opacity: 0.9;
              }
              .fc .fc-timegrid-event {
                border-radius: 4px;
              }
              .fc-h-event {
                border-width: 0 0 0 3px !important;
                border-style: solid !important;
              }
              .fc-h-event.habit-event {
                border-style: dashed !important;
              }
              .fc-h-event .fc-event-main {
                background: transparent !important;
              }
              .fc-daygrid-event {
                border-radius: 4px !important;
                margin-bottom: 1px;
              }
              .fc-daygrid-event .fc-event-main {
                padding: 2px 4px;
                background: transparent !important;
              }
              .fc-daygrid-block-event .fc-event-time,
              .fc-daygrid-block-event .fc-event-title {
                padding: 0;
              }
              .fc-daygrid-dot-event .fc-event-title {
                font-weight: 500;
              }
              .fc .fc-scrollgrid {
                border-color: rgba(255,255,255,0.1);
              }
              .fc .fc-timegrid-slot {
                height: ${slotHeight}px !important;
              }
              .fc .fc-timegrid-slot-lane {
                height: ${slotHeight}px !important;
              }
              .fc .fc-highlight {
                background: rgba(245,158,11,0.2);
              }
              .fc-col-header-cell {
                padding: 6px 0;
              }
              @media (min-width: 640px) {
                .fc-col-header-cell {
                  padding: 8px 0;
                }
              }
              .fc .fc-timegrid-slot-label-cushion {
                font-size: 10px;
              }
              @media (min-width: 640px) {
                .fc .fc-timegrid-slot-label-cushion {
                  font-size: 12px;
                }
              }
            `}</style>
);
